import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { MutateKeys } from "../constants/MutateKeys";
import { useMutation, useQuery } from "@tanstack/react-query";
import { generatePath } from "react-router-dom";
import { cleanParams } from "../Utilities/queryParamUtils";
import { ArchiveKeys } from "../enums/ArchiveActionKeys";
import {
  AddDatabaseTableEntriesParamsType,
  ContactTableServiceResponseType,
  DeleteDatabaseTableEntriesParamsType,
  DocumentsTableServiceResponseType,
  EditContactParamsType,
  FetchDatabaseTableType,
  JobDetailsDatabaseServiceResponseType,
  JobTableDataResponseType,
  JobTableServiceResponseType,
  NotesTableServiceResponseType,
  OrdersTableServiceResponseType,
  RolesMetaServiceResponseType,
  SendViaEmailParams,
} from "../Types/DatabaseTabTypes";
import {
  DatabaseMetaKeys,
  DatabaseMetricsKey,
  DatabaseSections,
  JobDatabaseFilterType,
} from "../enums/DatabaseEnums";
import {
  JobDatabaseListingPayloadType,
  JobFilterOptionPayloadType,
} from "../Types/jobDatabaseTypes";
import { updateSessionDatabasePagination } from "../utils/storageUtils";
import { message } from "antd";

const JobDatabaseService = () => {
  const {
    FETCH_JOB_DATABASE_LIST,
    FETCH_JOB_DATABASE_SUMMARY,
    FETCH_AFFECTED_JOB_PRICE_ERRORS,
    BLOCK_UNBLOCK_JOB,
    FETCH_JOB_DATABASE_FILTER,
    GET_AFFECTED_JOBS_COUNT,
    ACCEPT_REVISED_PRICE_ROUTE,
    REJECT_REVISED_PRICE_ROUTE,
    ARCHIVE_DATABASE_ENTRY,
    DATABASE_TABLE_ROUTE,
    FETCH_JOB_DETAILS_CARD_ROUTE,
    SEND_VIA_EMAIL_ROUTE,
    UPDATE_JOB_ORDER_STATUS,
    DATABASE_META_PAGINATION,
  } = ApiRoutes;

  const {
    FETCH_JOB_DATABASE_LISTING_KEY,
    FETCH_JOB_DATABASE_SUMMARY_KEY,
    FETCH_AFFECTED_JOB_COUNT_KEY,
    FETCH_ORDERS_TABLES_KEY,
    FETCH_JOB_DETAILS_KEY,
    GET_DATABASE_PAGINATION_META,
  } = QueryKeys;

  const {
    BLOCK_UNBLOCK_JOB_KEY,
    ARCHIVE_JOB_DATABASE_ENTRY_KEY,
    JOB_DATABASE_JOBNO_META_KEY,
    JOB_DATABASE_JOBNAME_META_KEY,
    JOB_DATABASE_CUSTOMER_META_KEY,
    JOB_DATABASE_JOBTYPE_META_KEY,
    JOB_DATABASE_EQUIPMENT_META_KEY,
    JOB_DATABASE_MATERIAL_CATERGORY_META_KEY,
    JOB_DATABASE_MATERIAL_TYPE_META_KEY,
    JOB_DATABASE_SALES_PERSON_META_KEY,
    SEND_VIA_EMAIL_KEY,
    BLOCK_UNBLOCK_JOB_ORDER_KEY,
    ACCEPT_REVISED_PRICE_KEY,
    REJECT_REVISED_PRICE_KEY,
  } = MutateKeys;

  const FetchJobDatabaseSummary = (
    payloadData: JobDatabaseListingPayloadType,
  ) => {
    const fetchJobDatabaseSummary = async () => {
      const { page, perPage, ...rest } = payloadData;
      const payload = {
        ...cleanParams(rest),
      };
      const { data } = await axiosInstance.post(
        `${FETCH_JOB_DATABASE_SUMMARY}?type=${DatabaseMetricsKey?.JOB_METRICS}`,
        payload,
      );
      return data?.data;
    };

    return useQuery({
      queryFn: fetchJobDatabaseSummary,
      queryKey: [FETCH_JOB_DATABASE_SUMMARY_KEY],
      refetchOnWindowFocus: false,
    });
  };

  const FetchJobDatabaseEntries = (
    payloadData: JobDatabaseListingPayloadType,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const fetchJobDatabaseEntries = async () => {
      const payload = {
        ...cleanParams(payloadData),
        setDefaultPageSize: pageSizeChanged,
      };
      const { data } = await axiosInstance.post(
        FETCH_JOB_DATABASE_LIST,
        payload,
      );
      return data;
    };

    return useQuery({
      queryFn: fetchJobDatabaseEntries,
      queryKey: [FETCH_JOB_DATABASE_LISTING_KEY, payloadData],
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!enabledFlag,
    });
  };

  const FetchAffectedMaterialPriceErrorEntries = (
    payloadData: JobDatabaseListingPayloadType,
  ) => {
    const fetchAffectedMaterialPriceErrorEntries = async () => {
      const payload = {
        ...cleanParams(payloadData),
      };
      const { data } = await axiosInstance.post(
        FETCH_AFFECTED_JOB_PRICE_ERRORS,
        payload,
      );
      return data;
    };

    return useQuery({
      queryFn: fetchAffectedMaterialPriceErrorEntries,
      queryKey: ["fetchAffectedMaterialPriceErrorEntries", payloadData],
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });
  };

  const FetchAffectedJobCount = () => {
    const fetchAffectedJobCount = async () => {
      const { data } = await axiosInstance.get(GET_AFFECTED_JOBS_COUNT);
      return data.data.count;
    };

    return useQuery({
      queryFn: fetchAffectedJobCount,
      queryKey: [FETCH_AFFECTED_JOB_COUNT_KEY],
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });
  };

  const AcceptRevisedPrice = () => {
    const acceptRevisedPrice = async ({
      id,
      orderId,
    }: {
      id: string;
      orderId: string;
    }) => {
      const { data } = await axiosInstance.put(
        generatePath(ACCEPT_REVISED_PRICE_ROUTE, {
          id,
          orderId,
        }),
      );
      return data;
    };

    return useMutation({
      mutationKey: [ACCEPT_REVISED_PRICE_KEY],
      mutationFn: acceptRevisedPrice,
    });
  };

  const RejectRevisedPrice = () => {
    const rejectRevisedPrice = async ({
      id,
      orderId,
      payload,
    }: {
      id: string;
      orderId: string;
      payload: {
        date: string;
        notes: string | null;
        documents: Array<{
          name: string;
          url: string;
        }>;
      };
    }) => {
      const { data } = await axiosInstance.post(
        generatePath(REJECT_REVISED_PRICE_ROUTE, { id, orderId }),
        payload,
      );
      return data;
    };

    return useMutation({
      mutationKey: [REJECT_REVISED_PRICE_KEY],
      mutationFn: rejectRevisedPrice,
    });
  };

  const ArchiveJobDatabaseEntry = () => {
    const archiveJobDatabaseEntry = async ({
      id,
      notes,
    }: {
      id: string;
      notes: string;
    }) => {
      const payload = {
        entityData: {
          notes: notes,
          deletedTable: ArchiveKeys.JOBS,
        },
      };
      const data = await axiosInstance.post(
        generatePath(ARCHIVE_DATABASE_ENTRY, { id: id }),
        payload,
      );
      return data;
    };

    return useMutation({
      mutationFn: archiveJobDatabaseEntry,
      mutationKey: [ARCHIVE_JOB_DATABASE_ENTRY_KEY],
    });
  };

  const FetchJobNumberFilterMeta = () => {
    const fetchJobNumberFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.jobNumber;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType.JOB_NUMBER,
        }),
        payload,
      );
      return data?.jobNumber;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_JOBNO_META_KEY],
      mutationFn: fetchJobNumberFilterMeta,
    });
  };

  const FetchJobNameFilterMeta = () => {
    const fetchJobNameFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.jobName;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType?.JOB_NAME,
        }),
        payload,
      );
      return data?.jobName;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_JOBNAME_META_KEY],
      mutationFn: fetchJobNameFilterMeta,
    });
  };

  const FetchCustomerFilterMeta = () => {
    const fetchCustomerFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.companyId;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType?.CUSTOMER,
        }),
        payload,
      );
      return data?.customer;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_CUSTOMER_META_KEY],
      mutationFn: fetchCustomerFilterMeta,
    });
  };

  const FetchJobTypeFilterMeta = () => {
    const fetchJobTypeFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.jobType;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType?.JOB_TYPE,
        }),
        payload,
      );
      return data?.jobType;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_JOBTYPE_META_KEY],
      mutationFn: fetchJobTypeFilterMeta,
    });
  };

  const FetchEquipmentFilterMeta = () => {
    const fetchEquipmentFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.equipment;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType?.EQUIPMENT,
        }),
        payload,
      );
      return data?.equipment;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_EQUIPMENT_META_KEY],
      mutationFn: fetchEquipmentFilterMeta,
    });
  };

  const FetchMaterialCatergoryFilterMeta = () => {
    const fetchMaterialCatergoryFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.materialCategory;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType?.MATERIAL_CATEGORY,
        }),
        payload,
      );
      return data?.materialCategory;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_MATERIAL_CATERGORY_META_KEY],
      mutationFn: fetchMaterialCatergoryFilterMeta,
    });
  };

  const FetchMaterialTypeFilterMeta = () => {
    const fetchMaterialTypeFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.materialType;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType?.MATERIAL_TYPE,
        }),
        payload,
      );
      return data?.materialType;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_MATERIAL_TYPE_META_KEY],
      mutationFn: fetchMaterialTypeFilterMeta,
    });
  };

  const FetchSalesPersonFilterMeta = () => {
    const fetchSalesPersonFilterMeta = async (
      params: JobFilterOptionPayloadType,
    ) => {
      delete params?.salesPersonId;
      const payload = {
        ...params,
      };
      const { data } = await axiosInstance.post(
        generatePath(FETCH_JOB_DATABASE_FILTER, {
          filterId: JobDatabaseFilterType?.SALES_PERSON,
        }),
        payload,
      );
      return data?.salesPerson;
    };

    return useMutation({
      mutationKey: [JOB_DATABASE_SALES_PERSON_META_KEY],
      mutationFn: fetchSalesPersonFilterMeta,
    });
  };

  const FetchOrderDetails = (params?: FetchDatabaseTableType) => {
    const fetchOrderDetails =
      async (): Promise<OrdersTableServiceResponseType> => {
        const url = generatePath(DATABASE_TABLE_ROUTE, {
          tableType: DatabaseSections.ORDERS,
        });
        const cleanedParams = cleanParams(params);
        const { data } =
          await axiosInstance.get<OrdersTableServiceResponseType>(url, {
            params: cleanedParams,
          });
        return data;
      };
    return useQuery({
      queryKey: [FETCH_ORDERS_TABLES_KEY, params],
      queryFn: fetchOrderDetails,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!params?.section_id,
    });
  };

  const BlockUnblockJobOrder = () => {
    const blockUnblockJobOrder = async ({ jobId, orderId, isBlocked }) => {
      const url = generatePath(UPDATE_JOB_ORDER_STATUS, {
        jobId: jobId,
        orderId: orderId,
      });
      const { data } = await axiosInstance.post(url, {
        isBlocked: isBlocked,
      });
      return data;
    };

    return useMutation({
      mutationKey: [BLOCK_UNBLOCK_JOB_ORDER_KEY],
      mutationFn: blockUnblockJobOrder,
    });
  };

  const FetchJobDetails = (id?: string) => {
    const fetchJobDetails =
      async (): Promise<JobDetailsDatabaseServiceResponseType> => {
        const url = generatePath(FETCH_JOB_DETAILS_CARD_ROUTE, {
          id: id,
        });
        const { data } =
          await axiosInstance.get<JobDetailsDatabaseServiceResponseType>(url);
        return data;
      };
    return useQuery({
      queryKey: [FETCH_JOB_DETAILS_KEY],
      queryFn: fetchJobDetails,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: !!id,
    });
  };

  const SendViaEmail = () => {
    const sendViaEmail = async (params: SendViaEmailParams) => {
      const url = generatePath(SEND_VIA_EMAIL_ROUTE, {
        id: params.id,
        orderId: params.orderId,
      });
      const { data } = await axiosInstance.post(url);
      return data;
    };
    return useMutation({
      mutationFn: sendViaEmail,
      mutationKey: [SEND_VIA_EMAIL_KEY],
    });
  };

  const GetPaginationDefaultMeta = () => {
    const getPaginationDefaultMeta = async () => {
      try {
        const { data } = await axiosInstance.post(
          DATABASE_META_PAGINATION,
          {},
          {
            params: {
              type: DatabaseMetaKeys.PAGINATION,
            },
          },
        );
        updateSessionDatabasePagination(data?.data);
        return data;
      } catch (error) {
        updateSessionDatabasePagination({});
        message.error("Failed to retrive page size defaults");
      }
    };

    return useQuery({
      queryKey: [GET_DATABASE_PAGINATION_META],
      queryFn: getPaginationDefaultMeta,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: true,
    });
  };

  const BlockUnblockJobs = () => {
    const blockUnblockJobs = async ({
      jobId,
      blockAction,
    }: {
      jobId: string;
      blockAction: boolean;
    }) => {
      const url = generatePath(BLOCK_UNBLOCK_JOB, { jobId: jobId });
      const { data } = await axiosInstance.post(url, {
        isBlocked: blockAction,
      });
      return data;
    };

    return useMutation({
      mutationKey: [BLOCK_UNBLOCK_JOB_KEY],
      mutationFn: blockUnblockJobs,
    });
  };

  return {
    FetchJobDatabaseSummary,
    FetchJobDatabaseEntries,
    FetchAffectedMaterialPriceErrorEntries,
    FetchAffectedJobCount,
    AcceptRevisedPrice,
    RejectRevisedPrice,
    ArchiveJobDatabaseEntry,
    FetchJobNumberFilterMeta,
    FetchJobNameFilterMeta,
    FetchCustomerFilterMeta,
    FetchJobTypeFilterMeta,
    FetchEquipmentFilterMeta,
    FetchMaterialCatergoryFilterMeta,
    FetchMaterialTypeFilterMeta,
    FetchSalesPersonFilterMeta,
    FetchOrderDetails,
    BlockUnblockJobOrder,
    FetchJobDetails,
    SendViaEmail,
    GetPaginationDefaultMeta,
    BlockUnblockJobs,
  };
};

export default JobDatabaseService;
